import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  ImageIcon,
  Hash,
  Zap,
  FileSearch,
} from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const STEPS = [
  {
    n: "01",
    icon: MessageCircle,
    title: "Open WhatsApp",
    body: "Message Fortiqa on WhatsApp. Tell us what happened in your own words — Swahili or English, doesn't matter.",
  },
  {
    n: "02",
    icon: ImageIcon,
    title: "Upload screenshots",
    body: "Share screenshots of the M-Pesa SMS, customer chat, or any other evidence. We accept images and PDFs.",
  },
  {
    n: "03",
    icon: Hash,
    title: "Include the transaction ID",
    body: "The M-Pesa reference (e.g. QXY12AB3CD) and the amount lost — both help our verification engine match the claim instantly.",
  },
  {
    n: "04",
    icon: FileSearch,
    title: "We review",
    body: "Our AI runs OCR + fraud-signal scoring + device-fingerprint checks. Edge cases route to a human SIU officer within minutes.",
  },
  {
    n: "05",
    icon: Zap,
    title: "Paid in seconds",
    body: "Clean claims auto-approve. Payout lands back in your M-Pesa till via B2C transfer — usually before you finish your chai.",
  },
];

export default function FlashClaim() {
  usePageMeta({
    title: "FlashClaim — paid in seconds, reviewed with intelligence",
    description:
      "Submit a Fortiqa claim through WhatsApp — screenshots, transaction IDs, and evidence. AI-assisted review, payout in seconds.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="flashclaim-page">
      <MarketingPageShell
        eyebrow="FlashClaim"
        title="Paid in seconds, reviewed with intelligence."
        intro="When fraud happens, every minute matters. FlashClaim turns a complicated insurance process into a five-step WhatsApp chat — and pays merchants out in under 90 seconds for clean claims."
        testid="flashclaim"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map(({ n, icon: Icon, title, body }) => (
            <div
              key={n}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#114232]/8 grid place-items-center">
                  <Icon className="w-4.5 h-4.5 text-[#114232]" strokeWidth={1.7} />
                </div>
                <div className="font-mono text-[#c15337] font-bold text-sm">{n}</div>
              </div>
              <div className="font-heading text-base font-bold text-[#114232] mb-1.5 leading-snug">
                {title}
              </div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{body}</p>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14">
          <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10">
            <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-3">
              What to have ready before you submit
            </div>
            <ul className="grid sm:grid-cols-2 gap-3 mt-5">
              {[
                "M-Pesa transaction ID (e.g. QXY12AB3CD)",
                "Screenshot of the M-Pesa confirmation SMS",
                "Customer WhatsApp / SMS conversation",
                "Date and approximate amount lost",
                "Delivery proof (if a physical good was shipped)",
                "Any related police OB number or Safaricom reversal request ID",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2.5 text-sm text-[#1b1d1c]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c15337] mt-2 shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Ready
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Start a claim now.
              </h3>
              <p className="text-[#f4f2eb]/80 mt-2 max-w-md text-sm sm:text-base">
                Open WhatsApp and tell us what happened. We acknowledge within seconds.
              </p>
            </div>
            <a
              href="https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20I%20need%20to%20start%20a%20claim"
              target="_blank"
              rel="noreferrer"
              data-testid="flashclaim-cta-start"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
            >
              <MessageCircle className="w-4 h-4" />
              Start a claim
            </a>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
